/**
 * React Query Hook: Metadata Deployments
 * 
 * Fetches metadata deployments filtered by status (for Success/Failed tabs)
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { graphqlRequest } from '@/services/deployment/client';
import { METADATA_DEPLOYMENTS_QUERY } from '@/services/deployment/queries';
import type {
  MetadataDeploymentsResponse,
  MetadataDeploymentsVariables,
  MetadataDeployment,
  DeploymentStatus,
} from '@/services/deployment/types';

/**
 * Fetch metadata deployments from API
 */
async function fetchMetadataDeployments(
  variables: MetadataDeploymentsVariables
): Promise<MetadataDeployment[]> {
  const response = await graphqlRequest<MetadataDeploymentsResponse>(
    METADATA_DEPLOYMENTS_QUERY,
    variables
  );
  return response.metadataDeployments;
}

/**
 * Hook to fetch metadata deployments by status
 * 
 * @param organizationId - Organization UUID
 * @param status - Deployment status filter (SUCCEEDED, FAILED, etc.)
 * @param options - Query options
 * @returns React Query result with deployment records
 */
export function useMetadataDeployments(
  organizationId: string,
  status?: DeploymentStatus,
  options?: {
    enabled?: boolean;
  }
): UseQueryResult<MetadataDeployment[], Error> {
  const { enabled = true } = options || {};

  return useQuery({
    queryKey: ['metadataDeployments', organizationId, status],
    queryFn: () =>
      fetchMetadataDeployments({
        organizationId,
        status,
      }),
    enabled: enabled && !!organizationId,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: true,
  });
}

